import { FC, useState, useMemo, useEffect } from 'react';
import { Grid, Typography } from '@mui/material';
import { useGuardianState } from '../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useLocation, useNavigate } from 'react-router-dom';
import TabGroup from '../../../../components/tabGroup';
import { TabItem } from '../../../../components/components.interfaces';
import { pages } from '../../../../../utils/common';
import GuardianEventsView from './subviews/events/guardianEvents.view';
import GuardianCommunicationsView from './subviews/communications/guardianCommunications.view';

const GuardianEventsCommunicationView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  const eventsCommTabs = useMemo<TabItem[]>(() => {
    const tabs: TabItem[] = [];
    const eventsCommPages = pages.guardian?.subPages?.['events-communication']?.subPages;

    if (eventsCommPages?.events) {
      tabs.push({
        label: translateText('guardian.eventsCommunication.events.title'),
        tabPanel: <GuardianEventsView />,
        onClickHandler: () => {
          navigate(`${pages.guardian.path}${pages.guardian.subPages?.['events-communication']?.path}${eventsCommPages.events.path}`);
        },
      });
    }
    if (eventsCommPages?.communications) {
      tabs.push({
        label: translateText('guardian.eventsCommunication.communications.title'),
        tabPanel: <GuardianCommunicationsView />,
        onClickHandler: () => {
          navigate(`${pages.guardian.path}${pages.guardian.subPages?.['events-communication']?.path}${eventsCommPages.communications.path}`);
        },
      });
    }
    return tabs;
  }, [translateText, navigate]);

  const currentRoute = useMemo(() => {
    const path = location.pathname;
    const eventsCommPath = `${pages.guardian.path}${pages.guardian.subPages?.['events-communication']?.path}`;
    if (path.startsWith(eventsCommPath)) {
      const subPath = path.replace(eventsCommPath, '');
      if (subPath.includes('/events') || subPath === '' || subPath === '/') {
        return 'events';
      }
      if (subPath.includes('/communications')) {
        return 'communications';
      }
    }
    return 'events';
  }, [location.pathname]);

  useEffect(() => {
    const routeMap: Record<string, number> = {
      events: 0,
      communications: 1,
    };
    const tabIndex = routeMap[currentRoute] ?? 0;
    if (tabIndex >= 0 && tabIndex < eventsCommTabs.length) {
      setSelectedTab(tabIndex);
    } else if (eventsCommTabs.length > 0) {
      const eventsCommPages = pages.guardian?.subPages?.['events-communication']?.subPages;
      if (eventsCommPages?.events) {
        navigate(`${pages.guardian.path}${pages.guardian.subPages?.['events-communication']?.path}${eventsCommPages.events.path}`, { replace: true });
      }
    }
  }, [currentRoute, eventsCommTabs, navigate]);

  const handleTabChange = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < eventsCommTabs.length && eventsCommTabs[tabIndex].onClickHandler) {
      eventsCommTabs[tabIndex].onClickHandler?.(String(tabIndex));
    }
  };

  return (
    <Grid
      container
      spacing={3}
      sx={{ p: 3, height: '100%' }}
      data-testid="guardian-events-communication-container"
    >
      <Grid item xs={12}>
        <Typography variant="h5">
          {translateText('guardian.eventsCommunication.title')}
        </Typography>
        {selectedChildId && (
          <Typography variant="body2" color="text.secondary">
            {translateText('guardian.eventsCommunication.selectedChild')}: {selectedChildId}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} sx={{ height: 'calc(100% - var(--main-title-height))', overflow: 'auto' }}>
        <TabGroup
          tabs={eventsCommTabs}
          initiallySelected={selectedTab}
          onTabChange={handleTabChange}
          dataTestIdPrefix="guardian-events-communication"
          limitHeight={true}
        />
      </Grid>
    </Grid>
  );
};

export default GuardianEventsCommunicationView;
